"use client";

import { useEffect, useState, type ReactNode } from "react";
import { AppHeader } from "@/components/shell/AppHeader";
import { BottomNav } from "@/components/shell/BottomNav";
import { DataPrefetcher } from "@/components/shell/DataPrefetcher";
import { WhatsNewSheet } from "@/components/shell/WhatsNewSheet";
import { PushEnableBanner } from "@/components/auth/PushEnableBanner";
import { RoleSetupDialog } from "@/components/auth/RoleSetupDialog";
import { MascotMoment } from "@/components/brand/MascotMoment";
import { useMascotMomentTrigger } from "@/lib/mascot/useMascotMomentTrigger";
import { useMemberSelfHeal } from "@/lib/hooks/useMemberSelfHeal";
import { useAppBadgeSync } from "@/lib/hooks/useAppBadgeSync";
import { useFcmAutoRegister } from "@/lib/hooks/useFcmAutoRegister";
import { useAuth } from "@/lib/auth/context";
import { useMembers } from "@/lib/hooks/useMembers";
import { updateMember } from "@/lib/db/members";
import { getUnseenEntries, markVersionSeen } from "@/lib/utils/whatsNew";
import type { ChangelogEntry } from "@/lib/changelog";

// Email verification jest teraz obsługiwana przez EmailVerifiedGate
// w AuthGuard (hard block przed wejściem do app), więc banner nie jest
// już potrzebny — rodzic bez verified maila w ogóle nie zobaczy AppShell.

export function AppShell({ children }: { children: ReactNode }) {
  // Self-heal dla rodzica — niezależnie od strony. Bez tego rodzic, który
  // wszedł od razu na np. /approvals (push z notyfikacji, deep-link), nie
  // miałby member doc i Cloud Function / Firestore rules zwracały 403.
  // Pokrywa też backfill isAdmin=true dla założyciela. No-op dla dzieci.
  useMemberSelfHeal();
  // Cichy refresh FCM tokena po zalogowaniu jeśli permission granted.
  // Bez tego rotowane tokeny FCM gubią się i push milczy.
  useFcmAutoRegister();
  // Liczba na ikonie home-screen (iOS PWA / Android Chrome PWA / desktop dock).
  // Foreground sync — background updatuje SW na podstawie push payload.
  useAppBadgeSync();

  const { active, dismiss } = useMascotMomentTrigger();

  // Role-picker dla założyciela — pokaże się raz, gdy rodzic-admin nie ma
  // jeszcze ustawionej relation. Po wyborze (Mama / Tata) zapisuje do
  // member doc, a parent-philosophy manifest pokazuje się dopiero potem
  // z poprawną maskotką (useMascotMomentTrigger ma blokadę na !relation).
  const { userDoc, firebaseUser } = useAuth();

  // Modal "Co nowego w Sianko" — auto-open po update aplikacji. Komputacja
  // gdy firebaseUser jest dostępny (potrzebne creationTime żeby rozróżnić
  // świeży signup od istniejącego usera bez lastSeen w LS). Pusta lista =
  // nie pokazujemy. Dismiss markuje current version jako widzianą.
  const [whatsNewEntries, setWhatsNewEntries] = useState<ChangelogEntry[]>([]);
  useEffect(() => {
    if (!firebaseUser) return;
    const createdAt = firebaseUser.metadata.creationTime
      ? new Date(firebaseUser.metadata.creationTime)
      : null;
    setWhatsNewEntries(
      getUnseenEntries(__APP_VERSION__, { userCreatedAt: createdAt }),
    );
  }, [firebaseUser]);
  const dismissWhatsNew = () => {
    markVersionSeen(__APP_VERSION__);
    setWhatsNewEntries([]);
  };
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;
  const { members } = useMembers(hid);
  const me = members.find((m) => m.userId === uid);
  const showRoleSetup =
    userDoc?.role === "parent" && me?.isAdmin === true && !me?.relation;
  const onRolePick = async (relation: "Mama" | "Tata") => {
    if (!hid || !uid) throw new Error("Brak sesji");
    await updateMember(hid, uid, { relation });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-bg text-ink">
      <DataPrefetcher />
      <AppHeader />
      <main
        className="flex-1 mx-auto w-full max-w-md px-4 py-5 space-y-4"
        style={{ paddingBottom: "calc(5rem + env(safe-area-inset-bottom))" }}
      >
        <PushEnableBanner />
        {children}
      </main>
      <BottomNav />

      {/* Globalne mascot moments (level-up, badge-unlocked).
          Renderowane TYLKO gdy role-picker nie jest aktywny — bez tego
          parent-philosophy mogłby się pojawić pod role-pickerem (z=60). */}
      {active && !showRoleSetup && (
        <MascotMoment
          context={active.context}
          open={true}
          onClose={dismiss}
          data={active.data}
          female={active.female}
        />
      )}

      <RoleSetupDialog open={showRoleSetup} onPick={onRolePick} />

      <WhatsNewSheet
        open={whatsNewEntries.length > 0}
        onClose={dismissWhatsNew}
        entries={whatsNewEntries}
      />
    </div>
  );
}
